import React, { useState, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Link } from 'react-router-dom';
import SVG from 'react-inlinesvg';
import ConfigContext from '../../../contexts/ConfigContext';
import { useClearAllNofiicationsMutation, useGetNotificationsQuery } from '../../../redux/slices/notificationSlice';
import { sanitizeHtml } from '../../../modules/formatUtils';
import useAuth from '../../../hooks/useAuth';

// Components
import styles from './Notifications.module.postcss';
import Tooltip from '../../shared/Tooltip';

dayjs.extend(relativeTime);

function Notifications({ truck }) {
  const { railsEnv } = useContext(ConfigContext);
  const { auth, cateringUser } = useAuth();

  const [open, setOpen] = useState(false);

  const { data: notifications = [], status, error, refetch } = useGetNotificationsQuery(
    { user_id: auth.userData.id },
    {
      pollingInterval: railsEnv === 'production' ? 30000 : 300000,
      skipPollingIfUnfocused: true,
    }
  );
  const [clearNotifications, { clearIsLoading }] = useClearAllNofiicationsMutation();

  const displayedNotifications = notifications.map((notification) => {
    let link = '';
    let { content } = notification;
    if (truck && notification.notifiable_type === 'Message') {
      link = `/vendor/truck/${notification.truck_slug || truck.id}`;
      content = (
        <div>{notification.content}:<br/><div dangerouslySetInnerHTML={{ __html: sanitizeHtml(notification.message) }}/></div>
      );
    } else if (cateringUser) {
      link = `/catering/requests/${notification.catering_request_id}`;
      link += `?bid=${notification.commentable_id}#${notification.notifiable_id}`;
    } else {
      link = `/vendor/truck/${notification.truck_id}`;
      link += `/catering-requests/${notification.catering_request_id}#${notification.notifiable_id}`;
    }
    return (
      <Link
        to={link}
        key={notification.id}
        className={styles.Notif}
      >
        <div className={styles.Notif_Content}>
          {content}
        </div>
        <div className={styles.Notif_Date}>
          {dayjs(notification.created_at).fromNow()}
        </div>
      </Link>
    );
  });

  const handleClearNotifications = useCallback(async () => {
    try {
      await clearNotifications({ user_id: auth.userData.id }).unwrap();
    } catch (err) {
      console.error('Failed to clear Notifications: ', err);
    }
  }, [clearNotifications, auth.userData.id]);

  return (
    <Tooltip
      placement='bottom-end'
      triggerOnClick
      lightTheme
      hideArrow
      offsetValue={10}
      parentClassName={styles.BellButton_Wrapper}
      onShow={() => setOpen(true)}
      onHide={() => setOpen(false)}
      content={(
        <div className={styles.Notif_PopUp}>
          <div className={styles.Notif_Header}>
            <h5 className='u-mb0 u-mt0'>
              Activity
            </h5>
            { displayedNotifications.length > 0 &&
              <button
                type='button'
                className='Button Button--small u-m0'
                onClick={handleClearNotifications}
              >
                Clear all
              </button>
            }
          </div>
          <div>
            {displayedNotifications}

            { displayedNotifications.length === 0 &&
              <span>
                No unread activity
              </span>
            }
          </div>
        </div>
      )}
    >
      <button
        type='button'
        className={cn(styles.BellButton, {
          [styles.BellButton_active]: open,
        })}
      >
        <SVG src='/static/images/icons/bell.svg' width='24' height='24'/>
        { displayedNotifications.length > 0 &&
          <span className={styles.Notif_Count}>
            {displayedNotifications.length}
          </span>
        }
      </button>
    </Tooltip>
  );
}

Notifications.propTypes = {
  truck: PropTypes.object,
};

export default Notifications;
